<template>
  <div class="full-height">
    <Spinner v-if="loading" />

    <div v-else-if="localConfig && localConfig.campaignData" class="full-height">
      <Card class="full-height">
        <!-- Name, type -->
        <div class="row">
          <!-- Name -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.name')"
                :validation="['required']"
                ref="data_name"
                v-if="isCreate || isEdit"
                v-model="name" />

              <OutputField
                :label="$t('campaignDesigner.campaignSetup.name')"
                :value="name"
                v-else />
            </div>
          </div>

          <!-- Campaign type selection -->
          <div class="slds-form-element slds-size--medium col">
            <CampaignTypeSelector
              ref="data_campaignType"
              v-if="isCreate && localConfig.campaignData.type.options.length > 1"
              v-model="campaignType"
              :options="localConfig.campaignData.type.options"
              :validation="['required']" />

            <OutputField :label="$t('campaignDesigner.campaignSetup.type.label')" type="custom" v-else>
              <span class="slds-swatch type-color-container" :style="`background:${campaignType.color ? campaignType.color: '#FFFFFF'};width:1.25rem;`" />
              <span :style="`width: -webkit-fill-available;`">{{campaignType.label}}</span>
            </OutputField>
          </div>
        </div>

        <!-- Masterkey, Market -->
        <div class="row">
          <!-- Masterkey campaign -->
          <div class="slds-form-element slds-size--medium col" v-if="masterKeys">
            <Picklist
              :label="$t('campaignDesigner.campaignSetup.masterKey')"
              :options="masterKeys"
              :validation="(isSales || isTechnical)? [] : ['required']"
              :show-empty-value="isSales || isTechnical"
              ref="data_masterkey"
              searchable
              v-if="isCreate || isEdit"
              v-model="masterKey" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.masterKey')"
              :value="masterKey"
              type="custom"
              v-else>

              <span v-if="masterKeys && masterKeys.length && masterKey">{{masterKey.length && masterKeys.find(mk => mk.value === masterKey).label}}</span>
            </OutputField>
          </div>

          <!-- Market -->
          <div class="slds-form-element slds-size--medium col" v-if="localConfig.campaignData.markets.options.length>1">
            <Picklist
              :can-remove-elements="true" 
              :label="$t('campaignDesigner.campaignSetup.market')"
              :hide-picklist="false"
              :options="localConfig.campaignData.markets.options"
              :validation="['required']"
              labels-prefix="markets"
              multipicklist
              ref="data_markets"
              v-if="(isCreate || isEdit)"
              v-model="markets" /> <!-- "isVHR()"" function was called to hide the element -->

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.market')"
              :options="localConfig.campaignData.markets.options"
              :value="markets"
              labels-prefix="markets"
              type="multipicklist"
              v-else />
          </div>
          <!-- Market Only One-->
          <div class="slds-form-element slds-size--medium col" v-else>
            <OutputField
              :label="$t('campaignDesigner.campaignSetup.market')"
              :options="localConfig.campaignData.markets.options"
              :value="localConfig.campaignData.markets.options[0]"
              labels-prefix="markets"
              type="multipicklist"/>
          </div>
        </div>

        <!-- Start date, End date -->
        <div class="row">
          <!-- Start date -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.startDate')"
                :validation="[{name: 'is-date-before', param: endDate}, 'is-date-after-today', 'required']"
                ref="data_startDate"
                type="date"
                :min="new Date()" 
                :max="this.endDate?this.endDate:null"
                v-if="isCreate || isEdit"
                v-model="startDate" />

              <OutputField
                :label="$t('campaignDesigner.campaignSetup.startDate')"
                :value="startDate"
                type="date"
                v-else />
            </div>
          </div>

          <!-- End date -->
          <div class="slds-form-element slds-size--medium col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :disabled="isContactPlan(campaignType.label)"
                :label="$t('campaignDesigner.campaignSetup.endDate')"
                :validation="isContactPlan(campaignType.label)? [] : [isSales ? '' : 'required', 'is-date-after-today']"
                ref="data_endDate"
                type="date"
                :min="this.startDate?this.startDate: new Date()"
                v-if="isCreate || isEdit"
                v-model="endDate" />

              <OutputField
                :label="$t('campaignDesigner.campaignSetup.endDate')"
                :value="endDate"
                type="date"
                v-else />
            </div>
          </div>
        </div>

        <!-- Brands, Channels -->
        <div class="row">
          <!-- Brands -->
          <div class="slds-form-element col slds-size--medium" v-if="localConfig.campaignData.campaignBrands.options.length>1">
            <Picklist
              :can-remove-elements="true"
              :hide-picklist="false"
              :label="$t('campaignDesigner.campaignSetup.brands')"
              :options="localConfig.campaignData.campaignBrands.options"
              :validation="['required']"
              labels-prefix="brands"
              multipicklist
              ref="data_brands"
              v-if="(isCreate || isEdit)"
              v-model="campaignBrands" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.brands')"
              :options="localConfig.campaignData.campaignBrands.options"
              :value="campaignBrands"
              labels-prefix="brands"
              type="multipicklist"
              v-else />
          </div>
          <!-- Brands only one -->
          <div class="slds-form-element col slds-size--medium" v-else>
            <OutputField
              :label="$t('campaignDesigner.campaignSetup.brand')"
              :options="localConfig.campaignData.campaignBrands.options"
              :value="localConfig.campaignData.campaignBrands.options[0]"
              labels-prefix="brands"
              type="multipicklist"/>
          </div>

          <!-- Channels -->
          <div class="slds-form-element col slds-size--medium" v-if="localConfig.campaignData.campaignChannel.options.length>1">
            <Picklist
              :label="$t('campaignDesigner.campaignSetup.channels.label')"
              :options="localConfig.campaignData.campaignChannel.options"
              :validation="['required']"
              labels-prefix="channels"
              multipicklist
              ref="data_channels"
              v-if="(isCreate || isEdit)"
              v-model="campaignChannel" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.channels.label')"
              :options="localConfig.campaignData.campaignChannel.options"
              :value="campaignChannel"
              labels-prefix="channels"
              type="multipicklist"
              v-else />
          </div>
          <!-- Channels only one-->
          <div class="slds-form-element col slds-size--medium" v-else>
            <OutputField
              :label="$t('campaignDesigner.campaignSetup.channel')"
              :validation="['required']"
              :options="localConfig.campaignData.campaignChannel.options"
              :value="localConfig.campaignData.campaignChannel.options[0]"
              labels-prefix="channels"
              type="multipicklist"/>
          </div>
        </div>

        <!-- Contribution Type, Content Classification -->
        <div class="row slds-m-top--xx-small" v-if="!isSales">
          <!-- Contribution Type -->
          <div class="slds-form-element col slds-size--medium">
            <GenericFilter
              filter-type="included"
              is-picklist
              labels-prefix="campaignDesigner.campaignSetup.contributionType"
              v-if="isCreate || isEdit"
              v-model="contributionType"
              :default-value="contributionTypeDefault"
              :options="localConfig.campaignData.contributionType.options"
              :label="$t('campaignDesigner.campaignSetup.contributionType.label')" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.contributionType.label')"
              :options="localConfig.campaignData.contributionType.options"
              :value="contributionType"
              labels-prefix="campaignDesigner.campaignSetup.contributionType"
              type="picklist"
              v-else />
          </div>

          <!-- Content Classification -->
          <div class="slds-form-element col slds-size--medium" v-if="localConfig.campaignData.contentClassification.options.length>1">
            <Picklist
              :label="$t('campaignDesigner.campaignSetup.contentClassification.label')"
              :options="localConfig.campaignData.contentClassification.options"
              :validation="['required']"
              labels-prefix="campaignDesigner.campaignSetup.contentClassification"
              ref="data_contentClassification"
              show-empty-value
              v-if="isCreate || isEdit"
              v-model="contentClassification" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.contentClassification.label')"
              :options="localConfig.campaignData.contentClassification.options"
              :value="contentClassification"
              labels-prefix="campaignDesigner.campaignSetup.contentClassification"
              type="picklist"
              v-else />
          </div>
          <!-- Content Classification Only One-->
          <div class="slds-form-element col slds-size--medium" v-else>
            <OutputField
              :label="$t('campaignDesigner.campaignSetup.contentClassification.label')"
              :options="localConfig.campaignData.contentClassification.options"
              :value="localConfig.campaignData.contentClassification.options[0]"
              labels-prefix="campaignDesigner.campaignSetup.contentClassification"
              type="picklist"/>
          </div>
        </div>

        <!-- Seed, Disable Orchestration -->
        <div class="row" v-if="!isSales">
          <!-- Seed -->
          <div class="slds-form-element col slds-size--medium">
            <Input
              :label="$t('campaignDesigner.campaignSetup.seed')"
              type="checkbox"
              v-if="isCreate || isEdit"
              v-model="seed" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.seed')"
              :value="seed"
              type="checkbox"
              v-else />
          </div>

          <!-- Orchestration -->
          <div class="slds-form-element col slds-size--medium">
            <OutputField
              :label="$t('campaignDesigner.targetDefinition.disableOrchestration')"
              :value="disableOrchestration"
              type="checkbox"
              v-if="!(isCreate || isEdit) || !disableOrchestrationEditable" />

            <Input
              :label="$t('campaignDesigner.targetDefinition.disableOrchestration')"
              type="checkbox"
              v-else
              v-model="disableOrchestration" />

          </div>
        </div>

        <!-- Segment Type / FInality-->
        <div class="row">
          <!-- Segment Type -->
          <div class="slds-form-element slds-size--medium col" >
            <Picklist
              :label="$t('campaignDesigner.campaignSetup.segmentType.label')"
              :options="localConfig.campaignData.segmentType.options"
              :validation="isSales ? [] : ['required']"
              labels-prefix="campaignDesigner.campaignSetup.segmentType"
              ref="data_segmentType"
              show-empty-value
              v-if="isCreate || isEdit"
              v-model="segmentType" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.segmentType.label')"
              :options="localConfig.campaignData.segmentType.options"
              :value="segmentType"
              labels-prefix="campaignDesigner.campaignSetup.segmentType"
              type="picklist"
              v-else />
          </div>

          <!-- Finality -->
          <div class="slds-form-element col slds-size--medium">
            <Picklist
              :label="$t('campaignDesigner.campaignSetup.finality.label')"
              :options="localConfig.campaignData.finality.options"
              :tooltip="finalityTooltip"
              :validation="isSales ? [] : ['required']"
              tooltip-direction="right"
              labels-prefix="campaignDesigner.campaignSetup.finality"
              ref="data_finality"
              show-empty-value
              v-if="isCreate || isEdit"
              v-model="finality" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.finality.label')"
              :options="localConfig.campaignData.finality.options"
              :value="finality"
              labels-prefix="campaignDesigner.campaignSetup.finality"
              type="picklist"
              v-else />
          </div>
        </div>
        
        <div class="row">
          <!-- CCDB Report Enabled -->
          <div class="slds-form-element col slds-size--medium" v-if="isSales">
            <Input
              :label="$t('campaignDesigner.campaignSetup.ccdbReportEnabled')"
              type="checkbox"
              v-if="isCreate || isEdit"
              v-model="ccdbReportEnabled" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.ccdbReportEnabled')"
              :value="ccdbReportEnabled"
              type="checkbox"
              v-else />

            <!-- CCDB Report Type -->
            <!--<Picklist
              :label="$t('campaignDesigner.campaignSetup.ccdbReportType.label')"
              :options="localConfig.campaignData.ccdbReportType.options"
              labels-prefix="campaignDesigner.campaignSetup.ccdbReportType"
              show-empty-value
              v-if="isCreate || isEdit"
              v-model="ccdbReportType" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.ccdbReportType.label')"
              :options="localConfig.campaignData.ccdbReportType.options"
              :value="ccdbReportType"
              labels-prefix="campaignDesigner.campaignSetup.ccdbReportType"
              type="picklist"
              v-else />-->
          </div>

            <!-- Manager ->
          <div class="slds-form-element slds-size--medium col" v-if="localConfig.campaignData.manager && localConfig.campaignData.manager.options.length>1 && isSales">
              <Picklist
                :label="$t('campaignDesigner.campaignSetup.manager.label')"
                :options="localConfig.campaignData.manager.options"
                labels-prefix="campaignDesigner.campaignSetup.manager"
                show-empty-value
                v-if="isCreate || isEdit"
                v-model="manager" />

              <OutputField
                :label="$t('campaignDesigner.campaignSetup.manager.label')"
                :options="localConfig.campaignData.manager.options"
                :value="manager"
                labels-prefix="campaignDesigner.campaignSetup.manager"
                type="picklist"
                v-else />
          </div>-->
          <!-- Manager Only one->
          <div class="slds-form-element slds-size--medium col" v-else-if="localConfig.campaignData.manager && isSales">
              <OutputField
                :label="$t('campaignDesigner.campaignSetup.manager.label')"
                :options="localConfig.campaignData.manager.options"
                :value="localConfig.campaignData.manager.options[0]"
                labels-prefix="campaignDesigner.campaignSetup.manager"
                type="picklist"/>
          </div>-->
        </div>

        <!-- Always on, Touchpoint -->
        <div class="row"  v-if="!isSales">
          <div class="slds-form-element col slds-size--medium">
            <Input
              :label="$t('campaignDesigner.campaignSetup.alwaysOn.label')"
              :tooltip="$t('campaignDesigner.campaignSetup.alwaysOn.tooltip')"
              tooltip-direction="right"
              type="checkbox"
              v-if="!this.isContactPlan(campaignType.label) && (isCreate || isEdit)"
              v-model="alwaysOn" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.alwaysOn.label')"
              :value="alwaysOn"
              type="checkbox"
              v-else />
          </div>

          <div class="slds-form-element slds-size--medium col" v-if="localConfig.campaignData.touchpoint">
            <Picklist
              :disabled="!isContactPlan(campaignType.label)"
              :label="$t('campaignDesigner.campaignSetup.touchpoint')"
              :options="localConfig.campaignData.touchpoint.options"
              :required="isCP"
              :validation="[{ name: 'conditional-required', param: isCP, message: 'validation.required'}]"
              ref="data_touchpoint"
              searchable
              show-empty-value
              v-if="isCreate || isEdit"
              v-model="touchpoint" />

            <OutputField
              :label="$t('campaignDesigner.campaignSetup.touchpoint')"
              :options="localConfig.campaignData.touchpoint.options"
              :value="touchpoint"
              type="picklist"
              v-else />
          </div>

        </div>

        <!-- Description, Deduplication -->
        <div class="row slds-m-top--small">
          <!-- Description -->
          <div class="slds-form-element col slds-size--medium">
            <div class="slds-form-element slds-size--medium">
              <TextArea
                :label="$t('campaignDesigner.campaignSetup.description')"
                v-if="isCreate || isEdit"
                v-model="description" />

              <OutputField
                :label="$t('campaignDesigner.campaignSetup.description')"
                :value="description"
                type="text"
                v-else />
            </div>
          </div>

          <!-- Deduplication -->
          <div class="slds-form-element col slds-size--medium" v-if="!isSales">
            <template v-if="(isCreate || isEdit)">
              <div class="slds-form-element__control">
                <div class="slds-checkbox">
                  <input type="checkbox" name="options" id="checkbox-deduplication" v-model="deduplicationEnabled" :disabled="false" />
                    <label class="slds-checkbox__label" for="checkbox-deduplication">
                    <span class="slds-checkbox_faux"></span>
                    <span class="slds-form-element__label">{{$t('campaignDesigner.targetDefinition.deduplication.label')}}</span>
                  </label>
                </div>
              </div>
              <RadioGroup
                :disabled="!deduplicationEnabled"
                :options="deduplicationRadioOptions"
                :validation="[{ name: 'conditional-required', param: deduplicationEnabled, message: 'validation.required'}]"
                no-label
                ref="data_deduplicationCriteria"
                v-model="deduplicationCriteria" />

              <Picklist
                :disabled="!deduplicationCriteria"
                :options="localConfig.campaignData.deduplicationParameter.options"
                :validation="[{ name: 'conditional-required', param: deduplicationEnabled, message: 'validation.required'}]"
                labels-prefix="campaignDesigner.targetDefinition.deduplication"
                no-label
                ref="data_deduplicationParameter"
                v-model="deduplicationParameter" />
            </template>

            <template v-else>
              <OutputField :label="$t('campaignDesigner.targetDefinition.deduplication.label')" type="custom">
                <span v-if="deduplicationCriteria">{{$t(`campaignDesigner.targetDefinition.deduplication.${deduplicationCriteria}`)}}</span>
              </OutputField>
              <OutputField no-label type="custom" v-if="deduplicationParameter">
                {{$t(`campaignDesigner.targetDefinition.deduplication.${deduplicationParameter}`)}}
              </OutputField>
            </template>
          </div>


        </div>

      </Card>

      <transition name="fade">
        <Modal
          @close="showFleetWarning = false"
          @positiveButtonClick="showFleetWarning = false"
          :positive-button-text="$t('general.ok')"
          :title="$t('campaignDesigner.campaignSetup.fleetWarning.title')"
          v-if="showFleetWarning">

          <div class="slds-text-align_center">
            {{$t('campaignDesigner.campaignSetup.fleetWarning.text')}}
          </div>
        </Modal>
      </transition>
    </div>
  </div>
</template>
<script>
import _ from 'lodash'
import { getEmptyConfigByKey as getEmptyConfig } from '../../utils/indexedDbUtils'
import { validate } from '../../utils/utils'
import CampaignTypeSelector from './campaign-setup/CampaignTypeSelector'
import Card from '../../slds/Card'
import GenericFilter from '../../campaign-manager/filters/GenericFilter'
import HasMode from '../../utils/HasMode'
import Input from '../../slds/Input'
import Modal from '../../Modal'
import moment from 'moment'
import OutputField from '../../slds/OutputField'
import Picklist from '../../slds/Picklist'
import RadioGroup from '../../slds/RadioGroup'
import Spinner from '../../Spinner'
import TextArea from '../../slds/TextArea'

const CONTACT_PLAN_CT = [
  'Contact Plan',
  'Service Contact Plan'
]

const FLEET_CT = [
  'Fleet & Business - Newsletter',
  'Fleet & Business - Commerciali'
]

export default {
  name: 'CampaignSetup',
  mixins: [HasMode],
  components: {
    Card,
    CampaignTypeSelector,
    GenericFilter,
    Input,
    Modal,
    OutputField,
    Picklist,
    RadioGroup,
    Spinner,
    TextArea
  },
  beforeCreate () {
    this.$emit('startLoading')
  },
  created () {
    this.callLoadFromStore()
    this.$emit('endLoading')
  },
  beforeDestroy () {
    this.alwaysOn = false
    this.campaignBrands = undefined
    this.campaignChannel = undefined
    this.campaignType = undefined
    this.contentClassification = undefined
    this.contributionType = undefined
    this.contributionTypeDefault = undefined
    this.deduplicationEnabled = undefined
    this.deduplicationCriteria = undefined
    this.deduplicationParameter = undefined
    this.deduplicationRadioOptions = undefined
    this.description = undefined
    this.disableOrchestration = undefined
    this.disableOrchestrationEditable = undefined
    this.endDate = undefined
    this.favorite = undefined
    this.finality = undefined
    this.fleetWarningShown = undefined
    this.localConfig = undefined
    this.markets = undefined
    this.masterKey = undefined
    this.name = undefined
    this.onlyHeader = undefined
    this.seed = undefined
    this.segmentType = undefined
    this.showFleetWarning = undefined
    this.startDate = undefined
    this.touchpoint = undefined
    this.type = undefined
  },
  computed: {
    finalityTooltip () {
      if (this.recordType === 'sales') {
        return this.$t('campaignDesigner.campaignSetup.finality.tooltip.sales')
      } else {
        return this.$t('campaignDesigner.campaignSetup.finality.tooltip.afterSales')
      }
    },
    isCP () {
      return this.isContactPlan(this.campaignType.label)
    },
    isSales () {
      return this.recordType === 'sales'
    },
    isTechnical () {
      return this.recordType === 'technical'
    }
  },
  data () {
    return {
      alwaysOn: false,
      campaignBrands: [],
      campaignChannel: [],
      campaignType: {},
      ccdbReportEnabled: true,
      ccdbReportType: '',
      contentClassification: '',
      contributionType: '',
      contributionTypeDefault: '',
      deduplicationEnabled: false,
      deduplicationCriteria: '',
      deduplicationParameter: '',
      deduplicationRadioOptions: [
        {
          label: this.$t('campaignDesigner.targetDefinition.deduplication.deduplication'),
          value: 'deduplication'
        },
        {
          label: this.$t('campaignDesigner.targetDefinition.deduplication.sorting'),
          value: 'sorting'
        }
      ],
      description: '',
      disableOrchestration: false,
      disableOrchestrationEditable: true,
      endDate: '',
      favorite: false,
      finality: '',
      fleetWarningShown: false,
      localConfig: {},
      manager: '',
      markets: [],
      masterKey: '',
      masterKeys: [],
      name: '',
      oldCampaignChannel: [],
      onlyHeader: false,
      recordType: '',
      seed: false,
      segmentType: '',
      showFleetWarning: false,
      startDate: '',
      touchpoint: '',
      type: ''
    }
  },
  methods: {
    async callLoadFromStore () {
      const campaign = this.loadFromStore('campaignData', ['loading', 'oldConfig'])

      if (campaign.config.userPreferences && campaign.config.userPreferences.fleetWarningShown !== undefined) {
        this.fleetWarningShown = !!campaign.config.userPreferences.fleetWarningShown
      }

      if (this.$route.query.type) {
        this.onlyHeader = this.$store.getters['settings/settings'].campaignRecordTypes[this.$route.query.type].onlyHeader
        this.recordType = this.$route.query.type
      }

      //console.log('>>>getters_full<<<< '+JSON.stringify(this.$store.getters));
      //console.log('>>>getters_campaignDesigner<<<< '+JSON.stringify(this.$store.getters['campaignDesigner']));

      //console.log('>>>getters_state<<<< '+JSON.stringify(this.$store.getters['campaignDesigner/state']));
      this.localConfig = this.$store.getters['campaignDesigner/state'].currentCampaignConfig

      //console.log('>>>this.localConfig.campaignData.masterKey<<<< '+JSON.stringify(this.localConfig.campaignData.masterKey));

      if (this.localConfig.campaignData.masterKey && this.localConfig.campaignData.masterKey.options) {
       // console.log('>>>if masterkey<<<< ');

        this.masterKeys = this.localConfig.campaignData.masterKey.options
      } else {
       // console.log('>>>else masterkey<<<< ');

        this.masterKeys = (await this.$store.dispatch('settings/getSettings')).masterKeys
      }

      if (this.localConfig.campaignData.type.options.length === 1) {
        const selectedType = this.localConfig.campaignData.type.options[0]
        this.campaignType = {
          label: selectedType.name,
          value: selectedType.code,
          color: selectedType.color,
          disableOrchestrationFlagEditable: selectedType.disableOrchestrationFlagEditable,
          disableOrchestrationFlagDefault: selectedType.disableOrchestrationFlagDefault
        }
        this.disableOrchestration = selectedType.disableOrchestrationFlagDefault
      }

      if (campaign.config.campaignData && campaign.config.campaignData.type && campaign.config.campaignData.type.selected) {
        const selectedType = campaign.config.campaignData.type.selected
        this.campaignType = {
          label: selectedType.name,
          value: selectedType.code,
          color: selectedType.color,
          disableOrchestrationFlagEditable: selectedType.disableOrchestrationFlagEditable,
          disableOrchestrationFlagDefault: selectedType.disableOrchestrationFlagDefault
        }
      }

      if (this.deduplicationCriteria) {
        this.deduplicationEnabled = true
      }

      const emptyConfig = (await getEmptyConfig(this.onlyHeader ? 'HEADER' : 'FULL')).config
      this.contributionTypeDefault = emptyConfig.campaignData.contributionType.selected
      if (!this.contributionType) {
        this.contributionType = ''
      }

      this.$nextTick(() => {
        // Restore disable orchestration flag
        if (campaign.disableOrchestration !== undefined) {
          this.disableOrchestration = campaign.disableOrchestration
        }
        // Restore always on
        if (campaign.alwaysOn !== undefined) {
          this.alwaysOn = campaign.alwaysOn
        }

        if (campaign.config.campaignData.seed.selected !== undefined) {
          this.seed = campaign.config.campaignData.seed.selected == 'true'
        }
      })
    },
    getSectionToSave () {
      const campaignType = {
        options: this.localConfig.campaignData.type.options,
        selected: this.localConfig.campaignData.type.options.find(type => type.code === this.campaignType.value)
      }

      let endDate = ''

      if (moment(this.endDate).isValid()) {
        endDate = moment(this.endDate).format('YYYY-MM-DD')
      }

      let contributionType

      if (this.contributionType) {
        contributionType = this.contributionType
      }

      const sectionToSave = {
        flat: {
          alwaysOn: this.alwaysOn,
          brands: this.localConfig.campaignData.campaignBrands.options.length==1?this.localConfig.campaignData.campaignBrands.options:this.campaignBrands,
          ccdbReportEnabled: this.ccdbReportEnabled,
          ccdbReportType: this.ccdbReportType,
          contentClassification: this.localConfig.campaignData.contentClassification.options.length==1?this.localConfig.campaignData.contentClassification.options[0]:this.contentClassification,
          contributionType,
          description: this.description,
          disableOrchestration: this.disableOrchestration,
          finality: this.finality,
          marketCodes: this.localConfig.campaignData.markets.options.length==1?this.localConfig.campaignData.markets.options:this.markets,
          endDate,
          manager: this.localConfig.campaignData.manager.options.length==1?this.localConfig.campaignData.manager.options[0]:this.manager,
          masterKey: this.masterKey,
          name: this.name,
          onlyHeader: this.onlyHeader,
          recordType: this.recordType,
          segmentType: this.segmentType,
          touchpoint: this.touchpoint,
          type: this.campaignType.value,
          startDate: moment(this.startDate).format('YYYY-MM-DD')
        },
        config: {
          campaignBrands: {
            options: this.localConfig.campaignData.campaignBrands.options,
            selected: this.localConfig.campaignData.campaignBrands.options.length==1?this.localConfig.campaignData.campaignBrands.options:this.campaignBrands
          },
          campaignChannel: {
            options: this.localConfig.campaignData.campaignChannel.options,
            selected: this.localConfig.campaignData.campaignChannel.options.length==1?this.localConfig.campaignData.campaignChannel.options:this.campaignChannel
          },
          contentClassification: {
            options: this.localConfig.campaignData.contentClassification.options,
            selected: this.localConfig.campaignData.contentClassification.options.length==1?this.localConfig.campaignData.contentClassification.options[0]:this.contentClassification
          },
          contributionType: {
            options: this.localConfig.campaignData.contributionType.options,
            selected: contributionType || null
          },
          deduplicationCriteria: {
            options: this.localConfig.campaignData.deduplicationCriteria.options,
            selected: this.deduplicationCriteria
          },
          deduplicationParameter: {
            options: this.localConfig.campaignData.deduplicationParameter.options,
            selected: this.deduplicationParameter
          },
          description: this.description,
          favorite: {
            options: this.localConfig.campaignData.favorite.options,
            selected: this.favorite
          },
          finality: {
            options: this.localConfig.campaignData.finality.options,
            selected: this.finality
          },
          markets: {
            options: this.localConfig.campaignData.markets.options,
            selected: this.localConfig.campaignData.markets.options.length==1?this.localConfig.campaignData.markets.options:this.markets
          },
          masterKey: {
            selected: this.masterKey
          },
          seed: {
            options: this.localConfig.campaignData.seed.options,
            selected: this.seed
          },
          segmentType: {
            options: this.localConfig.campaignData.segmentType.options,
            selected: this.segmentType
          },
          type: campaignType
        },
        userPreferences: {
          fleetWarningShown: this.fleetWarningShown
        }
      }

      if (this.localConfig.campaignData.touchpoint) {
        sectionToSave.config.touchpoint = {
          options: this.localConfig.campaignData.touchpoint.options,
          selected: this.touchpoint
        }
      }

      if (this.isSales && this.localConfig.campaignData.manager) {
        sectionToSave.config.manager = {
          options: this.localConfig.campaignData.manager.options,
          selected: this.localConfig.campaignData.manager.options.length==1?this.localConfig.campaignData.manager.options[0]:this.manager
        }
      }

      if (this.isSales && this.localConfig.campaignData.ccdbReportType) {
        sectionToSave.config.ccdbReportType = {
          options: this.localConfig.campaignData.ccdbReportType.options,
          selected: this.ccdbReportType
        }
      }
      return sectionToSave
    },
    isContactPlan (ct) {
      return CONTACT_PLAN_CT.includes(ct)
    },
    isFleet (ct) {
      return FLEET_CT.includes(ct)
    },
    validate () {
      if (this.isCreate || this.isEdit) {
        return validate(this, 'data', true)
      } else {
        const dateFrom = moment(this.startDate, 'YYYY-MM-DD')
        if (this.campaignStatus === 'Draft' && dateFrom.isBefore(moment(), 'day')) {
          this.showToast('error', this.$t('validation.invalidData'), 'text', this.$t('campaignDesigner.campaignSetup.validation.startDateMustBeInFuture'))
        } else {
          return true
        }
      }
    }
  },
  props: {
    campaignStatus: String,
    loading: Boolean,
    mode: String
  },
  watch: {
    campaignType: {
      handler (newValue, oldValue) {
        if (this.isContactPlan(newValue.label)) {
          this.alwaysOn = true
          this.endDate = ''
        } else if (this.isContactPlan(oldValue.label) && !this.isContactPlan(newValue.label)) {
          this.alwaysOn = false
        }

        if (this.isCreate) {
          if (!this.fleetWarningShown && this.isFleet(newValue.label) && !this.isFleet(oldValue.label)) {
            this.showFleetWarning = true
            this.fleetWarningShown = true
          } else if (!this.isFleet(newValue.label) && this.isFleet(oldValue.label)) {
            this.fleetWarningShown = false
          }
        }

        this.disableOrchestrationEditable = newValue.disableOrchestrationFlagEditable
        this.disableOrchestration = newValue.disableOrchestrationFlagDefault
      },
      deep: true
    },
    campaignChannel (newValue) {
      this.oldCampaignChannel = _.cloneDeep(newValue)
    },
    deduplicationEnabled (newValue) {
      if (!newValue) {
        this.deduplicationCriteria = ''
        this.deduplicationParameter = ''

        this.$refs.data_deduplicationCriteria.clearValidationError()
        this.$refs.data_deduplicationParameter.clearValidationError()
      }
    },
    endDate () {
      if (this.isCreate || this.isEdit) {
        this.$refs.data_startDate.clearValidationError()
      }
    },
    masterKey (value) {
      if (value === '') {
        this.masterKey = null
      }
    }
  }
}
</script>
<style lang="scss" scoped>
  .row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: flex-start;
    /*height: 5rem;*/
    margin-top: 2rem;
  }

  .col {
    flex-grow: 1;
  }

  .type-color-container {
    margin-right: 10px;
    width:1.5rem !important;
  }

  .full-height {
    height: 100%;
    overflow-y: auto;
  }

  Input {
    width: unset !important;
  }
</style>
